import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { catchError, filter, finalize, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { merge, omit } from 'lodash';

import { untilDestroyed, UrlParamsService } from '@app/core';
import { slideDownUpTrigger } from '@app/core/animations';
import { ContextEnum, DocumentStatusEnum, Folder, ListFormat, PublicFolder, SlimDocument, User } from '@app/models';
import { AppService, DocumentService, ErrorHandlerService, FolderService, NotyService, UserService } from '@app/services';
import { LoaderService } from '@app/shared';
import { DocumentsListingService } from '../shared/documents-listing.service';
import { FoldersListingService } from '../shared/folders-listing.service';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Location } from '@angular/common';

@Component({
  selector: 'app-documents-list',
  templateUrl: './documents-list.component.html',
  styleUrls: ['./documents-list.component.scss'],
  animations: [slideDownUpTrigger],
  standalone: false
})
export class DocumentsListComponent implements OnInit, OnDestroy {
  ListFormat = ListFormat;
  documents: SlimDocument[];
  documentsWithoutOrg: SlimDocument[];
  folders: Folder[];
  currentFolder: Folder;
  currentUser: User;
  isLoading = false;
  isLastPage = false;
  isLastPageFolders = false;
  pageNumber: number;
  pageNumberFolders: number;
  searchQuery: any;
  mobileSearch = '';
  isOnFolder: boolean;
  isSearchOpen = false;
  openTaxonomyMenu = false;
  isMobile = false;
  isShared = false;
  publicFolder: PublicFolder;
  protectedFolderForm: UntypedFormGroup;
  memberId: string = null;

  readonly DocumentStatusEnum = DocumentStatusEnum;

  constructor(
    public route: ActivatedRoute,
    public appService: AppService,
    public userService: UserService,
    public documentsListingService: DocumentsListingService,
    public foldersListingService: FoldersListingService,
    public urlParamsService: UrlParamsService,
    public translateService: TranslateService,
    private loaderService: LoaderService,
    private documentService: DocumentService,
    private folderService: FolderService,
    private errorHandlerService: ErrorHandlerService,
    private notyService: NotyService,
    private router: Router,
    private formBuilder: UntypedFormBuilder
  ) {
    this.protectedFolderForm = this.formBuilder.group({
      password: ''
    });
  }

  ngOnInit() {
    this.isMobile = window.innerWidth <= 992;

    const folderId = this.route.snapshot.params.folderId;
    this.isOnFolder = !!folderId;

    this.memberId = this.route.snapshot.params.memberId || null;

    of(null)
      .pipe(
        switchMap(() => this.checkIsOnFolder(folderId)),
        switchMap(publicFolder => this.checkProtectedFolder(publicFolder, folderId)),
        catchError(error => this.checkFolderPermission(error)),
        switchMap(folder => this.checkFolderData(folder)),
        tap(isUnblockedFolder => this.setUserData(isUnblockedFolder)),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        error: error => {
          this.errorHandlerService.handle(error);
        }
      });
  }

  ngOnDestroy() {}

  checkIsOnFolder(folderId: string) {
    if (this.isOnFolder) {
      this.isShared = this.route.snapshot.data.shared;
      if (this.isShared) return this.folderService.publicFolder({ publicId: folderId });
    } else {
      this.documents = [].constructor(this.appService.getFirstPageCount('mine') || 7);
      this.documentsWithoutOrg = [].constructor(this.appService.getFirstPageCount('noOrg') || 0);
    }
    return of(null);
  }

  checkProtectedFolder(publicFolder: PublicFolder | null, folderId: string | null) {
    if (!this.isOnFolder) {
      return of(null);
    }

    this.publicFolder = publicFolder;

    if (!this.isShared || !publicFolder?.has_password || publicFolder?.is_allowed_user) {
      return this.folderService.folder({ id: publicFolder ? publicFolder.id : folderId, memberId: this.memberId });
    }

    return of(null);
  }

  checkFolderPermission(error: any) {
    if (!this.isShared || !error.message.includes('invalid_password')) {
      this.router.navigate(['/pastas']);
    }
    this.errorHandlerService.handle(error);
    return of(null);
  }

  checkFolderData(folder: Folder | null) {
    if (!!folder) {
      this.documents = Array(7);
      this.publicFolder = null;
      this.currentFolder = folder;

      return of(true);
    }
    return of(!this.isOnFolder);
  }

  setUserData(isUnblockedFolder: boolean) {
    this.userService
      .watchCurrentUser()
      .pipe(
        filter(user => !!user),
        untilDestroyed(this)
      )
      .subscribe({
        next: user => {
          if (!(this.documents || [])[0]) {
            this.isSearchOpen = this.documentsListingService.isSearchParams(this.route.snapshot.queryParams);
            this.searchQuery = this.documentsListingService.searchQueryFromQueryParams(this.route.snapshot.queryParams);
          }

          if (this.currentUser && (this.currentUser.organization.id !== user.organization.id || this.currentUser.member.group.id !== user.member.group.id)) {
            this.currentUser = user; // required to have user before load
            if (!!isUnblockedFolder) {
              this.loadDocuments(undefined, { showLoader: true, isFromOnInit: true });
              this.loadFolders();
            }
          } else if (!this.currentUser) {
            this.currentUser = user; // required to have user before load
            if (!!isUnblockedFolder) {
              this.loadDocuments({ search: this.searchQuery }, { isFromOnInit: true });
              this.loadFolders({ search: this.searchQuery });
            }
          }
        }
      });
  }

  loadDocuments(options: { page?: number; search?: any } = {}, extraOptions: { showLoader?: boolean; isFromOnInit?: boolean } = {}) {
    this.isLoading = true;

    this.pageNumber = options.page || 1;
    const showLoader = extraOptions?.showLoader;
    if (showLoader) {
      this.loaderService.show();
    }

    if (!extraOptions?.isFromOnInit) {
      this.documentsListingService.pushSearchQueryToUrl(options.search);
    }

    this.documentService[this.isOnFolder ? 'folderDocuments' : 'unarchivedDocuments'](this.documentsSearchParams(options.search), this.pageNumber === 1 ? 'cache-and-network' : 'no-cache')
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.loaderService.hide();
        }),
        untilDestroyed(this)
      )
      .subscribe({
        next: page => {
          if (!!this.route.snapshot.params.folderId || (this.isSearchOpen && this.searchQuery)) {
            this.documents = page ? (this.pageNumber === 1 ? [] : this.documents || []).concat(page.data) : null;
          } else {
            this.documents = page ? (this.pageNumber === 1 ? [] : this.documents || []).concat(this.filterWithOrg(page.data)) : null;
            this.documentsWithoutOrg = page ? (this.pageNumber === 1 ? [] : this.documentsWithoutOrg || []).concat(this.filterWithoutOrg(page.data)) : null;
            if (this.pageNumber === 1) {
              this.appService.addFirstPageCounts({ mine: this.documents.length, noOrg: this.documentsWithoutOrg.length });
            }
          }
          this.isLastPage = !page || !page.has_more_pages;
        },
        error: error => {
          this.errorHandlerService.handle(error);
        }
      });
  }

  sign(documents: SlimDocument[]) {
    // showA3Option is used to hide the A3 option when more than one document is selected,
    // because the backend does not have a flow to sign more than one document at a time
    this.documentsListingService.sign(documents, { showA3Option: documents.length <= 1 }).subscribe({
      next: () => {
        if (documents.filter(doc => (doc as any)._isSelected).length > 0) {
          this.notyService.info(this.translateService.instant('couldnt_sign_documents_selected'));
        } else if (!this.route.snapshot.params.folderId) {
          const allDocuments = (this.documents || []).concat(this.documentsWithoutOrg || []);

          this.documents = this.filterWithOrg(allDocuments);
          this.documentsWithoutOrg = this.filterWithoutOrg(allDocuments);
        }
      },
      error: error => {
        this.errorHandlerService.handle(error);
      }
    });
  }

  delete(targetDocuments: SlimDocument[], documents: SlimDocument[]) {
    this.documentsListingService.delete(documents, targetDocuments, { context: ContextEnum.User, ...(this.isOnFolder && { folderId: this.currentFolder?.id }) }).subscribe({
      error: error => {
        this.errorHandlerService.handle(error);
      }
    });
  }

  transfer(documents: SlimDocument[], isRestoration?: boolean) {
    this.documentsListingService.transfer(this.documents, documents, { context: ContextEnum.User, isRestoration }).subscribe({
      next: () => this.loadDocuments({ page: 1, search: this.searchQuery }, { showLoader: true }),
      error: error => this.errorHandlerService.handle(error)
    });
  }

  loadFolders(options: { page?: number; search?: any } = {}) {
    if (this.isOnFolder) {
      this.isLoading = true;
      this.pageNumberFolders = options.page || 1;

      this.folderService
        .folders(
          {
            parentId: this.currentFolder?.id,
            memberId: this.memberId,
            search: (options.search || {}).name,
            limit: 14,
            page: this.pageNumberFolders,
            orderBy: { field: this.appService.listSorting, direction: this.appService.listDirection }
          },
          this.pageNumberFolders === 1 ? 'cache-and-network' : 'no-cache'
        )
        .pipe(finalize(() => (this.isLoading = false)))
        .subscribe({
          next: page => {
            this.folders = (this.pageNumberFolders === 1 ? [] : this.folders || []).concat(page.data);
            this.isLastPageFolders = !page || !page.has_more_pages;
          },
          error: error => {
            this.errorHandlerService.handle(error);
          }
        });
    }
  }

  openFoldersModal(options: { documents?: SlimDocument[]; folder?: Folder; isCurrentFolder?: boolean }) {
    if (options.documents) {
      this.documentsListingService
        .openFoldersModal(
          this.documents,
          options.documents,
          {
            currentFolderId: this.currentFolder && this.currentFolder.id,
            isCurrentFolder: options.isCurrentFolder
          },
          ContextEnum.User
        )
        .subscribe();
    } else if (options.folder) {
      this.foldersListingService.openFoldersModal(options.isCurrentFolder ? [] : this.folders, options.folder, false, this.currentFolder?.id).subscribe({
        next: folder => {
          if (options.isCurrentFolder) {
            this.currentFolder.parentFolder = folder;
          }
        },
        error: error => this.errorHandlerService.handle(error)
      });
    }
  }

  openCreateFolderModal() {
    this.foldersListingService.openCreateFolderModal({ parentId: this.currentFolder?.id }).subscribe({
      next: folder => this.folders.unshift(folder),
      error: error => this.errorHandlerService.handle(error)
    });
  }

  openUpdateFolderModal(folder: Folder, isCurrentFolder = false) {
    this.foldersListingService.openUpdateFolderModal(folder).subscribe({
      next: newFolder => {
        if (isCurrentFolder) {
          this.currentFolder.name = newFolder?.name;
        } else {
          merge(folder, newFolder);
        }
      },
      error: error => this.errorHandlerService.handle(error)
    });
  }

  openShareFolderModal(folder: Folder) {
    this.foldersListingService.openShareFolderModal({ folder, organizationId: this.currentUser.organization.id }).subscribe();
  }

  deleteFolder(folder: Folder) {
    this.foldersListingService.delete(this.folders, folder).subscribe({
      complete: () => this.notyService.success(this.translateService.instant('notyService.folderDeletedSuccess')),
      error: error => this.errorHandlerService.handle(error)
    });
  }

  deleteCurrentFolder() {
    this.foldersListingService.delete([], this.currentFolder).subscribe({
      complete: () => {
        this.router.navigate(['/pastas']);
        this.notyService.success(this.translateService.instant('notyService.folderDeletedSuccess'));
      },
      error: error => this.errorHandlerService.handle(error)
    });
  }

  resendSignatures(documents: SlimDocument[]) {
    this.documentsListingService.resendSignatures(documents).subscribe({
      next: () => this.notyService.success(this.translateService.instant('notyService.documentsResentToSignatories')),
      error: error => this.errorHandlerService.handle(error)
    });
  }

  toggleBlock(documents: SlimDocument[]) {
    this.documentsListingService.toggleBlock(documents).subscribe({
      error: error => this.errorHandlerService.handle(error)
    });
  }

  resendWebhook(document: SlimDocument) {
    this.documentService.resendWebhook({ documentId: document.id }).subscribe({
      next: () => this.notyService.success(this.translateService.instant('notyService.documentResendWebhook')),
      error: error => this.errorHandlerService.handle(error)
    });
  }

  clearFilters() {
    this.appService.documentStatusFilter = null;
    this.mobileSearch = '';
    this.searchQuery = null;
    this.isSearchOpen = false;
    this.loadDocuments({ page: 1 });
    this.loadFolders({ page: 1 });
  }

  toggleSearch(value: boolean) {
    if (this.documents) {
      this.isSearchOpen = value;
      if (this.isSearchOpen) {
        if (!this.isOnFolder) {
          if (this.documents.length === 0) this.loadDocuments({ search: ' ', page: 1 });
          this.documents.unshift(...(this.documentsWithoutOrg || []));
          this.documentsWithoutOrg = [];
        }
        this.searchQuery = { ...(this.searchQuery || {}), includeArchived: this.isOnFolder };
      } else {
        if (!this.isOnFolder) {
          this.loadDocuments({ page: 1 });
          this.documentsWithoutOrg = this.filterWithoutOrg(this.documents || []);
          this.documents = this.filterWithOrg(this.documents || []);
        }
        this.clearFilters();
      }
    }
  }

  exportDocuments(searchQuery: any) {
    if (!this.currentUser.email) {
      this.notyService.info(this.translateService.instant('cant_export_documents_selected_when_email_isnt_registered'));
      return;
    }

    this.isLoading = true;
    this.documentsListingService
      .exportDocuments(omit(this.documentsSearchParams(searchQuery), ['page', 'limit', 'sandbox']))
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        error: error => this.errorHandlerService.handle(error)
      });
  }

  isShowingDocumentsWithoutOrg() {
    return !this.isOnFolder && (this.documentsWithoutOrg || []).length > 0;
  }

  routeToNewDocument() {
    this.router.navigate(['/documentos/novo'], { state: { folder: this.currentFolder } });
  }

  unblockFolder() {
    if (this.protectedFolderForm.invalid) {
      this.protectedFolderForm.markAllAsTouched();
      return;
    }

    this.loaderService.show();
    const { password } = this.protectedFolderForm.getRawValue();

    this.folderService
      .folder({ id: this.publicFolder.id, password })
      .pipe(finalize(() => this.loaderService.hide()))
      .subscribe({
        next: data => {
          this.currentFolder = data;
          this.publicFolder = null;

          this.notyService.success(this.translateService.instant('documents.shareFolder.folderUnblocked'));

          this.loadDocuments({ page: 1 });
          this.loadFolders({ page: 1 });
        },
        error: error => this.errorHandlerService.handle(error)
      });
  }

  toggleSearchOpen() {
    this.isSearchOpen = !this.isSearchOpen;
    if (!this.isSearchOpen) {
      this.toggleSearch(false);
    }
  }

  changeStatusFilter(status: DocumentStatusEnum) {
    this.appService.documentStatusFilter = status;
    this.loadDocuments({ page: 1, search: this.searchQuery });
  }

  isInternalUser(user?: User) {
    user = user || this.currentUser;
    return !!user && /@(autentique\.com\.br|pipefy\.com)$/.test(user.email || '');
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.isMobile = window.innerWidth <= 992;
  }

  protected filterWithOrg(documents: SlimDocument[]) {
    return documents.filter(doc => {
      const signer = (doc.signatures || []).find(sig => sig.user && this.currentUser && sig.user.id === this.currentUser.id);
      const watcher = (doc.watchers || []).find(watcher => watcher.user_id && this.currentUser && watcher.user_id === this.currentUser.id);
      return (signer && !!signer.organization_id) || (watcher && !!watcher.organization_id);
    });
  }

  protected filterWithoutOrg(documents: SlimDocument[]) {
    return documents.filter(doc => {
      const signer = (doc.signatures || []).find(sig => sig.user && this.currentUser && sig.user.id === this.currentUser.id);
      const watcher = (doc.watchers || []).find(watcher => watcher.user_id && this.currentUser && watcher.user_id === this.currentUser.id);
      return (!signer || !signer.organization_id) && (!watcher || !watcher.organization_id);
    });
  }

  private documentsSearchParams(searchQuery: any) {
    return {
      folderId: this.currentFolder?.id ?? this.route.snapshot.params.folderId,
      memberId: this.memberId,
      status: this.appService.documentStatusFilter,
      limit: this.documentsListingService.perPage,
      page: this.pageNumber,
      context: (searchQuery || {}).groupUuid ? ContextEnum.Group : ContextEnum.User,
      sandbox: this.appService.showSandbox,
      orderBy: { field: this.appService.listSorting, direction: this.appService.listDirection },
      ...(Object.keys(searchQuery || {}).length > 0 ? { status: undefined, ...searchQuery } : {})
    };
  }
}
